import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";

// Lote 160 — peças visuais comuns da área Compartilhamentos. Todo estado tem TEXTO (nunca só cor):
// verde suave = acesso ativo/aceite, âmbar = pendência, cinza = o resto. Vermelho fica para ações destrutivas.
export const dataBR = (iso) => (iso ? formatDate(String(iso).slice(0, 10)) : "");
export const dataHoraBR = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return dataBR(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const TOM = {
  ativo: "bg-emerald-50 text-emerald-800 border-emerald-200",
  aguardando_aceite: "bg-amber-50 text-amber-800 border-amber-200",
};
export function EstadoTag({ estado, rotulo, testid, className = "" }) {
  return (
    <span
      data-testid={testid}
      data-estado={estado}
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${TOM[estado] || "bg-stone-100 text-stone-600 border-stone-200"} ${className}`}
    >
      {rotulo}
    </span>
  );
}

// Estado do ATO (separado do acesso). Verde = aceito/conferido; âmbar = pede ação de alguém; cinza = o resto.
export function AtoTag({ estado, rotulo, testid, className = "" }) {
  const tom = ["aceito", "conferido"].includes(estado) ? "ativo" : ["aguardando_resposta", "reconfirmar", "realizacao_informada"].includes(estado) ? "aguardando_aceite" : "outro";
  return <EstadoTag estado={tom} rotulo={rotulo} testid={testid} className={className} />;
}
export const NOME_RESULTADO = { realizado: "Realizado", nao_realizado: "Não realizado" };

export const ESTADOS_FILTRO = [
  ["", "Todas as situações"], ["aguardando_aceite", "Aguardando aceite"], ["ativo", "Acesso ativo"], ["recusado", "Recusado"],
  ["convite_expirado", "Convite expirado"], ["acesso_expirado", "Acesso expirado"], ["encerrado", "Acesso encerrado"],
];

export function CopiarNumero({ valor, testid }) {
  const [ok, setOk] = useState(false);
  if (!valor) return null;
  return (
    <button
      type="button"
      data-testid={testid}
      aria-label="Copiar número do processo"
      title="Copiar número do processo"
      onClick={async (e) => {
        e.stopPropagation();
        try { await navigator.clipboard.writeText(valor); setOk(true); setTimeout(() => setOk(false), 1500); } catch { toast.error("Não consegui copiar"); }
      }}
      className="inline-flex items-center justify-center h-6 w-6 rounded text-stone-500 hover:bg-stone-100"
    >
      {ok ? <Check className="w-3.5 h-3.5 text-emerald-700" strokeWidth={2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />}
    </button>
  );
}

// Uma linha do evento. `detalhado` mostra partes, número completo (com copiar), vara e comarca.
export function ItemEvento({ it, detalhado = false, testid }) {
  if (it.removido || it.indisponivel) {
    return <div data-testid={testid} className="text-sm text-stone-500 italic">{it.titulo}</div>;
  }
  return (
    <div data-testid={testid} className="text-sm">
      <div className="font-semibold text-stone-900">
        {it.titulo}{it.cancelado ? <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-stone-500">Cancelado</span> : null}
        <span className="ml-2 font-normal text-stone-600 tabular-nums">{dataBR(it.data)}{it.hora ? ` às ${it.hora}` : ""}</span>
      </div>
      {detalhado && (
        <div className="text-xs text-stone-600 mt-0.5 space-y-0.5">
          {it.partes && <div>{it.partes}</div>}
          {it.numero_processo && <div className="flex items-center gap-1 tabular-nums break-all">{it.numero_processo}<CopiarNumero valor={it.numero_processo} testid={`copiar-${it.ref}`} /></div>}
          {(it.vara || it.comarca) && <div>{[it.vara, it.comarca].filter(Boolean).join(" · ")}</div>}
          {it.endereco && <div>{it.endereco}</div>}
        </div>
      )}
    </div>
  );
}

// Lote 162 — um processo compartilhado. Antes do aceite chega só `numero_mascarado`; depois, dados básicos + as categorias liberadas.
export function ItemProcesso({ it, testid }) {
  if (it.removido || it.indisponivel) {
    return <div data-testid={testid} className="text-sm text-stone-500 italic">{it.titulo}</div>;
  }
  if (it.numero_mascarado) {
    return <div data-testid={testid} className="text-sm"><b>Processo</b> <span className="text-stone-600 tabular-nums">{it.numero_mascarado}</span></div>;
  }
  return (
    <div data-testid={testid} className="text-sm space-y-2">
      <div>
        <div className="flex items-center gap-1 font-semibold text-stone-900 tabular-nums break-all">{it.numero}<CopiarNumero valor={it.numero} testid={`copiar-${it.ref}`} /></div>
        <div className="text-xs text-stone-600 space-y-0.5">
          {it.partes && <div>{it.partes}</div>}
          {(it.vara || it.comarca) && <div>{[it.vara, it.comarca].filter(Boolean).join(" · ")}</div>}
        </div>
      </div>
      {it.eventos && (
        <div data-testid={testid ? `${testid}-eventos` : undefined}>
          <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-1">Audiências e prazos</div>
          {it.eventos.length === 0 ? <p className="text-xs text-stone-500 italic">Nenhum evento agendado neste processo.</p> : (
            <ul className="space-y-1.5">{it.eventos.map((e) => <li key={e.ref}><ItemEvento it={e} testid={testid ? `${testid}-ev-${e.ref}` : undefined} /></li>)}</ul>
          )}
        </div>
      )}
      {it.andamentos && (
        <div data-testid={testid ? `${testid}-andamentos` : undefined}>
          <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-1">Andamentos</div>
          {it.andamentos.length === 0 ? <p className="text-xs text-stone-500 italic">Nenhum andamento oficial registrado.</p> : (
            <ul className="space-y-1.5">
              {it.andamentos.map((a) => (
                <li key={a.id} className="text-xs">
                  <span className="tabular-nums text-stone-500">{dataBR(a.data)}{a.hora ? ` ${a.hora}` : ""}</span> <span className="text-[10px] font-bold uppercase tracking-wide text-stone-500">{a.origem}</span>
                  <div className="text-stone-800 break-words">{a.descricao}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// Bolinha de novidades (comentário novo / proposta aguardando decisão). Sempre com TEXTO, nunca só cor.
export function NovidadeTag({ n, testid }) {
  if (!n) return null;
  return <span data-testid={testid} className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#c8502d] text-white text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">{n} {n === 1 ? "novidade" : "novidades"}</span>;
}

export const ACOES_HISTORICO = {
  criado: "Compartilhamento criado", email_enviado_ao_servico: "E-mail entregue ao serviço de envio", email_falhou: "Falha no envio do e-mail",
  whatsapp_preparado: "Mensagem preparada no WhatsApp (envio feito por você; não é possível confirmar)", disponivel_no_app: "Convite disponível no app",
  aceito: "Convite aceito", recusado: "Convite recusado", encerrado: "Acesso encerrado", agenda_ligada: "Passou a aparecer na agenda do parceiro",
  agenda_desligada: "Deixou de aparecer na agenda do parceiro",
  codigo_enviado: "Código de acesso enviado ao e-mail do convite", codigo_falhou: "Falha ao enviar o código de acesso", convidado_entrou: "Convidado entrou com o código",
  ato_aceito: "Ato aceito pelo parceiro", ato_recusado: "Ato recusado pelo parceiro", ato_reconfirmado: "Parceiro confirmou de novo após mudança nos eventos",
  ato_realizacao_informada: "Parceiro informou a realização do ato", ato_conferido: "Realização conferida", ato_nao_conferido: "Realização devolvida ao parceiro",
  comentou: "Comentário na conversa", proposta_enviada: "Proposta de alteração enviada", proposta_aceita: "Proposta aceita", proposta_recusada: "Proposta recusada",
  proposta_cancelada: "Proposta cancelada pelo parceiro", aviso_dono_enviado_ao_servico: "Aviso por e-mail entregue ao serviço de envio", aviso_dono_falhou: "Falha no aviso por e-mail",
};
